/**
 * Shared drawing utilities for the procedural scene engine.
 *
 * Rules every procedural scene follows:
 *  1. All motion is periodic with the loop length, so the clip is
 *     genuinely seamless when repeated (spec section 16/17).
 *  2. No per-frame allocation of large objects - mobile GPUs and the
 *     compositor both hate churn.
 *  3. Audio reactivity is subtle and additive, never a music visualiser.
 */

import { createRng } from '../../utils/rng.js';

/** Re-exported so scene modules only import from one place. */
export { createRng };

const noiseCache = new Map();

/**
 * A small reusable noise tile. Drawing 128x128 grain and letting the
 * canvas scale it is dramatically cheaper than writing per-pixel noise
 * into a 1080x1920 buffer every frame.
 */
export function getNoiseTile(size = 128) {
  if (noiseCache.has(size)) return noiseCache.get(size);
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  const image = ctx.createImageData(size, size);
  const rng = createRng(0xb0b0);
  for (let i = 0; i < image.data.length; i += 4) {
    const v = 110 + Math.floor(rng() * 90);
    image.data[i] = v;
    image.data[i + 1] = v;
    image.data[i + 2] = v;
    image.data[i + 3] = 255;
  }
  ctx.putImageData(image, 0, 0);
  noiseCache.set(size, canvas);
  return canvas;
}

/** Film grain overlay. Cheap because the tile is cached and scaled. */
export function drawGrain(ctx, w, h, { t = 0, alpha = 0.035, scale = 3 } = {}) {
  const tile = getNoiseTile();
  const offsetX = -((t * 37) % 128);
  const offsetY = -((t * 61) % 128);
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.globalCompositeOperation = 'overlay';
  for (let x = offsetX; x < w; x += 128 * scale) {
    for (let y = offsetY; y < h; y += 128 * scale) {
      ctx.drawImage(tile, x, y, 128 * scale, 128 * scale);
    }
  }
  ctx.restore();
}

export function drawVignette(ctx, w, h, strength = 0.55) {
  const gradient = ctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.2, w / 2, h / 2, Math.max(w, h) * 0.75);
  gradient.addColorStop(0, 'rgba(0,0,0,0)');
  gradient.addColorStop(1, `rgba(0,0,0,${strength})`);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, w, h);
}

export function drawLetterboxGradient(ctx, w, h, topAlpha = 0.5, bottomAlpha = 0.6) {
  const top = ctx.createLinearGradient(0, 0, 0, h * 0.3);
  top.addColorStop(0, `rgba(0,0,0,${topAlpha})`);
  top.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = top;
  ctx.fillRect(0, 0, w, h * 0.3);

  const bottom = ctx.createLinearGradient(0, h, 0, h * 0.65);
  bottom.addColorStop(0, `rgba(0,0,0,${bottomAlpha})`);
  bottom.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = bottom;
  ctx.fillRect(0, h * 0.65, w, h * 0.35);
}

/**
 * Soft radial light. Used for every glow in the library - a real
 * shadowBlur on a 1080x1920 canvas is far too expensive per frame.
 */
export function glow(ctx, x, y, radius, color, alpha = 1) {
  const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
  gradient.addColorStop(0, withAlpha(color, alpha));
  gradient.addColorStop(0.45, withAlpha(color, alpha * 0.35));
  gradient.addColorStop(1, withAlpha(color, 0));
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fill();
}

/** '#RRGGBB' + alpha -> 'rgba(r,g,b,a)'. Accepts '#rgb' too. */
export function withAlpha(hex, alpha) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r},${g},${b},${Math.max(0, Math.min(1, alpha))})`;
}

export function hexToRgb(hex) {
  let value = String(hex).replace('#', '');
  if (value.length === 3) value = value.split('').map((c) => c + c).join('');
  const int = Number.parseInt(value, 16);
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}

/** Smooth 0..1..0 pulse that completes exactly `cycles` times per loop. */
export function loopPulse(phase, cycles = 1) {
  return 0.5 - 0.5 * Math.cos(phase * Math.PI * 2 * cycles);
}

/** Deterministic "random" value for index i - stable across frames. */
export function hash01(i, salt = 1) {
  const x = Math.sin(i * 12.9898 + salt * 78.233) * 43758.5453;
  return x - Math.floor(x);
}

/**
 * Blur a region without filter support cost: draw the source region
 * scaled down and back up. `ctx.filter` is supported but slow on some
 * Android GPUs, so procedural scenes avoid it.
 */
export function softBlurRegion(ctx, source, sx, sy, sw, sh, dx, dy, dw, dh, steps = 2) {
  let tmpW = Math.max(1, Math.round(sw / 8));
  let tmpH = Math.max(1, Math.round(sh / 8));
  const tmp = document.createElement('canvas');
  tmp.width = tmpW;
  tmp.height = tmpH;
  const tctx = tmp.getContext('2d');
  tctx.drawImage(source, sx, sy, sw, sh, 0, 0, tmpW, tmpH);
  for (let i = 1; i < steps; i += 1) {
    tctx.drawImage(tmp, 0, 0, tmpW, tmpH, 0, 0, tmpW, tmpH);
  }
  ctx.drawImage(tmp, 0, 0, tmpW, tmpH, dx, dy, dw, dh);
  tmpW = 0;
  tmpH = 0;
}

/** Normalised audio descriptor with a safe default when audio is absent. */
export const SILENT_AUDIO = { level: 0, bass: 0, mid: 0, treble: 0, beat: 0 };

/** Scale an audio value by the user's reactivity strength (0..1). */
export function reactive(value, strength) {
  return (Number(value) || 0) * Math.max(0, Math.min(1, strength));
}
